// AuthContext.js
import React, { createContext, useCallback, useState, useEffect } from "react";
import { supabase } from "./supabase"; // Your Supabase initialization

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [createCredits, setCreateCredits] = useState(null);

  useEffect(() => {
    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        setSession(session);
      })
      .finally(() => {
        setAuthLoading(false);
      });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setAuthLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const getUserCreateCredits = useCallback(
    async (retryCount = 0) => {
      if (session?.user?.id) {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/credits-balance`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: session.access_token,
              },
              body: "{}",
            }
          );
          if (!response.ok) {
            throw new Error(`Credits request failed (${response.status})`);
          }
          const data = await response.json();
          return data.generation_jobs_count;
        } catch (error) {
          if (retryCount < 3) {
            return await getUserCreateCredits(retryCount + 1);
          } else {
            console.error("Error fetching user credits after retries", error);
            return null;
          }
        }
      }
      return null;
    },
    [session]
  );

  useEffect(() => {
    if (session) {
      getUserCreateCredits().then((credits) => {
        setCreateCredits(credits);
      });
    }
  }, [session, getUserCreateCredits]);

  // Lets pages that spend credits (image generation) show an up-to-date
  // balance without waiting for the next sign-in.
  const refreshCredits = useCallback(async () => {
    const credits = await getUserCreateCredits();
    if (credits !== null) {
      setCreateCredits(credits);
    }
    return credits;
  }, [getUserCreateCredits]);

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error("Error signing out:", error);
    } else {
      setSession(null);
    }
  };

  return (
    <AuthContext.Provider
      value={{ session, authLoading, supabase, signOut, createCredits, refreshCredits }}
    >
      {children}
    </AuthContext.Provider>
  );
};
